// src/pages/landing/PagesListPage.tsx
// /landing — landing pages hub. Browse, create, publish, archive, duplicate,
// delete. ListLandingPages, CreateLandingPage (dialog), PublishLandingPage,
// ArchiveLandingPage, DuplicateLandingPage, DeleteLandingPage.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert, Input } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AnchoredMenu, MenuItem } from '@/components/AnchoredMenu';
import { landingApi } from '@core/api';
import type { LandingPage, LandingPageStatus } from '@core/types/landing';
import { useScope } from '@/hooks/useScope';
import { CreatePageDialog, LANDING_STATUSES, errMessage, formatDate, listParams, statusBadgeVariant } from '@/features/landing';

export function PagesListPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<LandingPageStatus | ''>('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<LandingPage | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    landingApi
      .ListLandingPages(listParams({ limit: 50 }, { ...(status ? { status } : {}), ...(search ? { search } : {}) }))
      .then((res) => setPages(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('landing.pages.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async (p: LandingPage) => {
    if (!p.entity?.id) return;
    try {
      const res = await landingApi.PublishLandingPage(p.entity.id, {});
      const updated = res.page;
      if (updated) setPages((prev) => prev.map((x) => (x.entity?.id === updated.entity?.id ? updated : x)));
    } catch (e) {
      setError(errMessage(e, t('landing.pages.publishError')));
    }
  };

  const archive = async (p: LandingPage) => {
    if (!p.entity?.id) return;
    try {
      const res = await landingApi.ArchiveLandingPage(p.entity.id, {});
      const updated = res.page;
      if (updated) setPages((prev) => prev.map((x) => (x.entity?.id === updated.entity?.id ? updated : x)));
    } catch (e) {
      setError(errMessage(e, t('landing.pages.archiveError')));
    }
  };

  const duplicate = async (p: LandingPage) => {
    if (!p.entity?.id) return;
    try {
      const res = await landingApi.DuplicateLandingPage(p.entity.id, {});
      const created = res.page;
      if (created) setPages((prev) => [created, ...prev]);
    } catch (e) {
      setError(errMessage(e, t('landing.pages.duplicateError')));
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete?.entity?.id) return;
    setDeleting(true);
    try {
      await landingApi.DeleteLandingPage(confirmDelete.entity.id);
      setPages((prev) => prev.filter((p) => p.entity?.id !== confirmDelete.entity?.id));
    } catch (e) {
      setError(errMessage(e, t('landing.pages.deleteError')));
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('landing.pages.title')}</h1>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/landing/templates" className="text-sm text-primary hover:underline">
            {t('landing.nav.templates')}
          </Link>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            {t('landing.pages.create')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder={t('landing.pages.searchPlaceholder')}
          className="max-w-sm"
        />
        <Select aria-label={t('landing.pages.status')} value={status} onChange={(e) => setStatus(e.target.value as LandingPageStatus | '')} className="max-w-xs">
          <option value="">{t('landing.pages.allStatuses')}</option>
          {LANDING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`landing.status.${s}`)}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="secondary" onClick={load}>
          {t('common.search')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {loading && (
        <div className="space-y-2" aria-busy="true">
          <div className="h-16 animate-pulse rounded-md bg-muted" />
          <div className="h-16 animate-pulse rounded-md bg-muted" />
        </div>
      )}

      {!loading && pages.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('landing.pages.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('landing.pages.subtitle')}</p>
          <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
            {t('landing.pages.create')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {pages.map((p) => (
          <div key={p.entity?.id} className="flex items-center gap-2 rounded-md border border-border p-3 hover:border-primary">
            <button onClick={() => navigate(`/landing/${p.entity?.id}`)} className="min-w-0 flex-1 text-left">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{p.title}</span>
                <Badge variant={statusBadgeVariant(p.status)}>{t(`landing.status.${p.status}`)}</Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">/l/{p.slug}</p>
              <p className="text-xs text-muted-foreground">{formatDate(p.updated_at, locale)}</p>
            </button>
            <PageRowMenu
              page={p}
              onPublish={() => publish(p)}
              onArchive={() => archive(p)}
              onDuplicate={() => duplicate(p)}
              onDelete={() => setConfirmDelete(p)}
            />
          </div>
        ))}
      </div>

      <CreatePageDialog open={createOpen} onClose={() => setCreateOpen(false)} scope={scope} onCreated={(p) => navigate(`/landing/${p.entity?.id}`)} />

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('landing.pages.deleteConfirmTitle')}
        description={t('landing.pages.deleteConfirmBody', { title: confirmDelete?.title ?? '' })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

/**
 * Row actions. A component per row rather than one menu driven by an open-id:
 * AnchoredMenu anchors to a ref, and a ref has to belong to a single trigger.
 */
function PageRowMenu({
  page,
  onPublish,
  onArchive,
  onDuplicate,
  onDelete,
}: {
  page: LandingPage;
  onPublish: () => void;
  onArchive: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t('common.moreActions')}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="3" cy="8" r="1.3" />
          <circle cx="8" cy="8" r="1.3" />
          <circle cx="13" cy="8" r="1.3" />
        </svg>
      </button>
      <AnchoredMenu
        anchor={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        align="end"
        ariaLabel={t('common.moreActions')}
        className="w-40 p-1"
      >
        {page.status !== 'LANDING_PAGE_STATUS_PUBLISHED' && (
          <MenuItem label={t('landing.pages.publish')} onSelect={() => run(onPublish)} />
        )}
        {page.status !== 'LANDING_PAGE_STATUS_ARCHIVED' && (
          <MenuItem label={t('landing.pages.archive')} onSelect={() => run(onArchive)} />
        )}
        <MenuItem label={t('common.duplicate')} onSelect={() => run(onDuplicate)} />
        <MenuItem label={t('common.delete')} destructive onSelect={() => run(onDelete)} />
      </AnchoredMenu>
    </div>
  );
}
